import { Trans } from "@lingui/react/macro";

import type { MFAMethod } from "../../../api";
import type { LoginOutcome } from "../../../api/api-connector";

import { ApiConnector } from "../../../api/api-connector";
import { MfaEnrollment } from "../mfa/mfa-enrollment";
import { MfaVerification } from "../mfa/mfa-verification";

/** The second factor a login still owes before it can issue a session. */
export type LoginChallenge =
  | { kind: "enrollment"; challengeId: string }
  | { kind: "verification"; challengeId: string; methods: MFAMethod[] };

/**
 * The second factor standing between a login and a session — the same screens
 * and the same wording however the login started. Finishing always means the
 * same thing, so only leaving is the caller's to decide.
 */
export function LoginChallenge({
  api,
  challenge,
  onCancel,
}: {
  api: ApiConnector;
  challenge: LoginChallenge;
  onCancel: () => void;
}) {
  switch (challenge.kind) {
    case "enrollment":
      return (
        <MfaEnrollment
          api={api}
          challengeId={challenge.challengeId}
          intro={
            <Trans>
              Your account requires two-factor authentication. Scan this QR code
              with an authenticator app, then enter the code it shows.
            </Trans>
          }
          expiredMessage={
            <Trans>
              Your login expired before two-factor setup was finished.
            </Trans>
          }
          cancelLabel={<Trans>Back to log in</Trans>}
          onEnrolled={() => {
            api.completeLogin();
          }}
          onCancel={onCancel}
        />
      );

    case "verification":
      return (
        <MfaVerification
          api={api}
          challengeId={challenge.challengeId}
          methods={challenge.methods}
          onVerified={() => {
            api.completeLogin();
          }}
          onCancel={onCancel}
          cancelLabel={<Trans>Back to log in</Trans>}
        />
      );
  }
}

/** What a login still needs from the user, beyond the credentials. */
export type LoginNext = LoginChallenge | { kind: "enrollmentOffer" };

/**
 * What a login still needs, or null when the app can just open. Both endpoints
 * answer with the same union, so one mapping serves the password form and the
 * emailed link alike.
 */
export function nextAfterLogin(outcome: LoginOutcome): LoginNext | null {
  switch (outcome.status) {
    case "mfa_enrollment_required":
      return { kind: "enrollment", challengeId: outcome.challenge_id };

    case "mfa_required":
      return {
        kind: "verification",
        challengeId: outcome.challenge_id,
        methods: outcome.methods,
      };

    case "success":
      // The session is issued either way. While the grace period lasts, an
      // account that will soon need two-factor is offered it rather than held
      // to it — past that date the login would have answered with the
      // enrollment challenge above instead.
      return outcome.mfa_enrollment_recommended
        ? { kind: "enrollmentOffer" }
        : null;
  }
}

/**
 * Floors, not fixed widths: the QR code and the backup-code row overflow the
 * column a login form sits in rather than reflowing. Spread into the route's
 * own layout classes, so these screens keep their widths wherever they appear.
 */
export const loginNextLayout: Record<LoginNext["kind"], string> = {
  enrollment: "min-w-96 max-w-full",
  verification: "min-w-80 max-w-full",
  enrollmentOffer: "min-w-96 max-w-full",
};
